import { Controller } from "@hotwired/stimulus"

/**
 * Roster Selection Controller
 *
 * Owns "who is selected" on the Team page, including the members the browser has never rendered.
 * Ticking every box on a page offers to extend the selection to the whole filtered roster; from
 * then on the selection is described to the server as "everything matching these filters, except
 * these ids" so it keeps meaning the same thing across pages.
 *
 * It lives outside the Turbo frame that holds the table, so the banner arriving with a re-filtered
 * page is the signal that the roster changed and the selection has to start over.
 */
export default class extends Controller {
  static targets = ["banner", "message", "selectAllButton", "clearButton"]

  connect() {
    this.reset()
    this._onChange = this.onCheckboxChange.bind(this)
    this.element.addEventListener("change", this._onChange)
  }

  disconnect() {
    this.element.removeEventListener("change", this._onChange)
  }

  bannerTargetConnected() {
    this.reset()
    this.render()
  }

  reset() {
    this.allMatching = false
    this.excludedIds = new Set()
  }

  // bulk-select ticks the page's boxes itself when the header box is used, which fires no change
  // event of their own — so the header box is watched as the page-wide signal it is.
  onCheckboxChange(event) {
    const checkbox = event.target
    if (!checkbox.matches(".bulk-select-checkbox")) return

    if (checkbox.matches('[data-bulk-select-target="selectAll"]')) {
      if (!checkbox.checked) this.reset()
      this.render()
      return
    }

    if (this.allMatching && checkbox.checked) {
      this.excludedIds.delete(checkbox.value)
    } else if (this.allMatching) {
      this.excludedIds.add(checkbox.value)
    }

    this.render()
  }

  selectAllMatching() {
    this.allMatching = true
    this.excludedIds = new Set()
    this.pageCheckboxes().forEach((checkbox) => { checkbox.checked = true })
    this.syncBulkSelect()
    this.render()
  }

  clear() {
    this.reset()
    this.bulkSelectController()?.clearSelection()
    this.render()
  }

  // --- what the server needs to know -----------------------------------------

  selectedCount() {
    if (this.allMatching) return Math.max(this.matchingCount() - this.excludedIds.size, 0)

    return this.checkedIds().length
  }

  hasSelection() {
    return this.selectedCount() > 0
  }

  // The filters travel with every selection, not just an "all matching" one: the server resolves
  // both forms against the filtered roster, so a selection made while looking at deactivated
  // members has to say so or none of those ids will be found.
  appendTo(formData) {
    this.appendFilters(formData)

    if (this.allMatching) {
      formData.append("selection[all_matching]", "true")
      this.excludedIds.forEach((id) => formData.append("selection[excluded_ids][]", id))
      return formData
    }

    this.checkedIds().forEach((id) => formData.append("company_user_ids[]", id))
    return formData
  }

  appendFilters(formData) {
    Object.entries(this.filterParams()).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((entry) => formData.append(`${key}[]`, entry))
      } else {
        formData.append(key, value)
      }
    })
  }

  filterParams() {
    if (!this.hasBannerTarget) return {}

    try {
      return JSON.parse(this.bannerTarget.dataset.filterParams || "{}")
    } catch {
      return {}
    }
  }

  // --- rendering ---------------------------------------------------------------

  render() {
    if (!this.hasBannerTarget) return

    const matching = this.matchingCount()
    const checked = this.checkedIds().length
    const selectable = this.selectableCheckboxes()
    const wholePageChecked = selectable.length > 0 && checked >= selectable.length

    if (this.allMatching) {
      this.showBanner(`All ${this.selectedCount()} members matching these filters are selected.`, {
        offerSelectAll: false
      })
      return
    }

    if (wholePageChecked && matching > selectable.length) {
      this.showBanner(`All ${checked} members on this page are selected.`, { offerSelectAll: true })
      return
    }

    this.bannerTarget.hidden = true
  }

  showBanner(message, { offerSelectAll }) {
    this.bannerTarget.hidden = false
    this.messageTarget.textContent = message

    if (this.hasSelectAllButtonTarget) this.selectAllButtonTarget.hidden = !offerSelectAll
    if (this.hasClearButtonTarget) this.clearButtonTarget.hidden = offerSelectAll
  }

  matchingCount() {
    if (!this.hasBannerTarget) return 0

    return parseInt(this.bannerTarget.dataset.matchingCount, 10) || 0
  }

  // --- the checkboxes bulk-select renders --------------------------------------

  pageCheckboxes() {
    return [...this.element.querySelectorAll('[data-bulk-select-target="checkbox"]')]
  }

  selectableCheckboxes() {
    return this.pageCheckboxes().filter((checkbox) => !checkbox.disabled)
  }

  checkedIds() {
    return this.selectableCheckboxes().filter((box) => box.checked).map((box) => box.value)
  }

  bulkSelectController() {
    const element = this.element.querySelector('[data-controller~="bulk-select"]')
    if (!element) return null

    return this.application.getControllerForElementAndIdentifier(element, "bulk-select")
  }

  syncBulkSelect() {
    const controller = this.bulkSelectController()
    controller?.syncSelectAll()
    controller?.updateBar()
  }
}
